<template>
  <PartsChromeLink v-if="to" :to="to" class="action-btn" :class="directionClass">
    <span v-if="arrowBefore" class="action-btn_arrow" aria-hidden="true"></span>
    {{ label }}
    <span v-if="!arrowBefore" class="action-btn_arrow" aria-hidden="true"></span>
  </PartsChromeLink>

  <button v-else :type="type" class="action-btn" :class="directionClass" @click="emit('click')">
    <span v-if="arrowBefore" class="action-btn_arrow" aria-hidden="true"></span>
    {{ label }}
    <span v-if="!arrowBefore" class="action-btn_arrow" aria-hidden="true"></span>
  </button>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `action-btn`（前へ／次へ）。支給101本中36本・55箇所に出る。
 *
 * **矢印の位置は向きで決まる。** 支給101本の全数集計で `_next` は 33/33 が文字の後ろ、
 * `_prev` は 18件中 17件が文字の前（`design-src/20260810/survey/survey_confirm.html` ほか）。
 *
 * **唯一の例外が `gp-member.html`（`/special-message`）で、`_prev` なのに矢印が文字の後ろ。**
 * これは書き間違いではなく、支給CSS が `.main_gp-member .action-btn_prev .action-btn_arrow`
 * に `transform: rotate(45deg)` を当てて**矢印を右向きに戻している**（`renewal/style.css:10181-10183`）。
 * ページ固有の意図的な扱いなので、`arrow` で位置だけ上書きできるようにしてある。
 *
 * **`<a>` と `<button>` の両方がある**（`<button>` 版は9本。フォームの送信・戻る）。
 * `to` があればリンク、無ければボタンにする（`parts/btn-round.vue` と同じ作り）。
 * リンクは `PartsChromeLink` を通す — 飛び先が未定の `'#'` や外部URLが混ざるため。
 *
 * `action-btn_arrow` は空 span のCSS矢印。支給には無いが装飾なので `aria-hidden` を足す。
 * 複数並べるときは `PartsActionBtns` で囲む。
 */
type Props = {
  label: string;
  /** リンクにする場合の飛び先。省略すると `<button>` になる */
  to?: string;
  /** 矢印の向き。`_prev` / `_next` のクラスを決める */
  direction?: 'next' | 'prev';
  /**
   * 矢印を文字のどちら側に置くか。既定は `direction` から決める（`prev` なら前・`next` なら後ろ）。
   * `/special-message` のように支給が向きと位置を独立させている画面でだけ渡す。
   */
  arrow?: 'before' | 'after';
  /** `<button>` のときの type */
  type?: 'button' | 'submit';
};

const props = withDefaults(defineProps<Props>(), {
  to: '',
  direction: 'next',
  arrow: undefined,
  type: 'button',
});

const emit = defineEmits<{ click: [] }>();

const arrowBefore = computed<boolean>(() =>
  props.arrow ? props.arrow === 'before' : props.direction === 'prev'
);
const directionClass = computed<string>(() => `action-btn_${props.direction}`);
</script>
